import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Helmet } from 'react-helmet'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Autocomplete from '@material-ui/lab/Autocomplete'
import InfoBox from 'components/info-box'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/styles'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import FacilitiesAlgolia from 'components/coaches-profile/profile-components/FacilitiesAlgolia'
import usStates from 'utils/usStates'
import timezones from 'utils/timezones'
import { FormActionBar, CtrlTextField } from 'components/form-util'
import ContentSection from 'components/content-section'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'

const validationSchema = yup.object().shape({
  facility: yup.object().shape(
    {
      name: yup
        .string()
        .label('Facility Name')
        .required(),
      address1: yup
        .string()
        .nullable()
        .label('Address')
        .when(['city', 'state', 'zip'], {
          is: (city, state, zip) => city || state || zip,
          then: yup.string().required(),
        }),
      city: yup
        .string()
        .nullable()
        .label('City')
        .when(['address1', 'state', 'zip'], {
          is: (address1, state, zip) => address1 || state || zip,
          then: yup.string().required(),
        }),
      state: yup
        .string()
        .nullable()
        .label('State')
        .when(['address1', 'city', 'zip'], {
          is: (address1, city, zip) => address1 || city || zip,
          then: yup.string().required(),
        }),
      zip: yup
        .string()
        .nullable()
        .label('Zip Code')
        .when(['address1', 'city', 'state'], {
          is: (address1, city, state) => address1 || city || state,
          then: yup.string().required(),
        }),
      timezone: yup
        .string()
        .nullable()
        .label('Time Zone'),
      phone: yup
        .string()
        .nullable()
        .label('Phone Number'),
      url: yup
        .string()
        .url()
        .nullable()
        .label('Website'),
    },
    [
      ['address1', 'city'],
      ['address1', 'state'],
      ['address1', 'zip'],
      ['city', 'state'],
      ['city', 'zip'],
      ['state', 'zip'],
    ],
  ),
})

const useStyles = makeStyles(theme => ({
  facilityInput: {
    marginBottom: `${theme.spacing(2)}px`,
  },
  buttonWrapper: {
    position: 'relative',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}))

const preExistingFacilityText =
  'Editing pre-existing facility information is disabled.'

const FormSections = ({
  control,
  register,
  setFacility,
  errors,
  preventExternalFacilityEdit,
  facilityPersisted = false,
  clearSelectedFacility,
}) => {
  const styles = useStyles()

  return (
    <>
      <ContentSection title="Name">
        <Controller
          name="facility.name"
          control={control}
          render={({ name, value, ref, onChange, onBlur }) =>
            facilityPersisted ? (
              <TextField
                required
                name={name}
                data-cy="facility.name"
                label="Facility Name"
                variant="outlined"
                className={styles.facilityInput}
                error={!!errors.facility?.name}
                helperText={errors.facility?.name?.message}
                inputRef={ref}
                value={value}
                onChange={onChange}
                autoComplete="off"
              />
            ) : (
              <FacilitiesAlgolia
                name={name}
                onSelect={setFacility}
                value={value}
                onBlur={onBlur}
                error={!!errors.facility?.name}
                helperText={errors.facility?.name?.message}
                inputVariant="outlined"
                onChange={onChange}
                disabled={preventExternalFacilityEdit}
              />
            )
          }
        />
        <input
          name="facility.externalFacilityId"
          type="hidden"
          ref={register}
        />
        {preventExternalFacilityEdit && (
          <Button
            variant="outlined"
            size="large"
            color="primary"
            style={{ margin: '20px 0px' }}
            onClick={clearSelectedFacility}
          >
            Clear Selection
          </Button>
        )}
      </ContentSection>
      <ContentSection title="Address">
        {preventExternalFacilityEdit && (
          <InfoBox text={preExistingFacilityText} maxWidth="xs" />
        )}
        <Grid container spacing={2}>
          <Grid item md={6} sm={12} xs={12}>
            <Controller
              name="facility.address1"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <TextField
                  required
                  name={name}
                  data-cy="facility.address1"
                  label="Address"
                  variant="outlined"
                  className={styles.facilityInput}
                  error={!!errors.facility?.address1}
                  helperText={errors.facility?.address1?.message}
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item md={6} sm={12} xs={12}>
            <Controller
              name="facility.address2"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <TextField
                  name={name}
                  data-cy="facility.address2"
                  label="Address 2"
                  variant="outlined"
                  className={styles.facilityInput}
                  error={!!errors.facility?.address2}
                  helperText={errors.facility?.address2?.message}
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item md={2} sm={12} xs={12}>
            <Controller
              name="facility.city"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <TextField
                  required
                  name={name}
                  data-cy="facility.city"
                  label="City"
                  variant="outlined"
                  className={styles.facilityInput}
                  error={!!errors.facility?.city}
                  helperText={errors.facility?.city?.message}
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
          <Grid item md={2} sm={12} xs={12}>
            <Controller
              name="facility.state"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <Autocomplete
                  name={name}
                  data-cy="facility.state"
                  disableClearable
                  required
                  value={value}
                  options={usStates}
                  onChange={(e, value) => onChange(value)}
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  className={styles.facilityInput}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="State"
                      variant="outlined"
                      error={!!errors.facility?.state}
                      helperText={errors.facility?.state?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item md={2} sm={12} xs={12}>
            <CtrlTextField
              name="facility.zip"
              data-cy="facility.zip"
              control={control}
              errors={errors}
              disabled={preventExternalFacilityEdit}
              label="Zip"
              required
              variant="outlined"
              className={styles.facilityInput}
            />
          </Grid>
        </Grid>
        {!preventExternalFacilityEdit && (
          <Grid container spacing={2}>
            <Grid item md={2} sm={12} xs={12}>
              <Controller
                name="facility.timezone"
                control={control}
                render={({ name, value, ref, onChange }) => (
                  <Autocomplete
                    name={name}
                    data-cy="facility.timezone"
                    disableClearable
                    required
                    value={value}
                    options={timezones}
                    onChange={(e, value) => onChange(value)}
                    inputRef={ref}
                    disabled={preventExternalFacilityEdit}
                    className={styles.facilityInput}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Time Zone"
                        variant="outlined"
                        error={!!errors.facility?.timezone}
                        helperText={errors.facility?.timezone?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </ContentSection>
      <ContentSection title="Additional Information">
        {preventExternalFacilityEdit && (
          <InfoBox text={preExistingFacilityText} maxWidth="xs" />
        )}
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <Controller
              name="facility.phone"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <TextField
                  name={name}
                  data-cy="facility.phone"
                  label="Phone (optional)"
                  variant="outlined"
                  className={styles.facilityInput}
                  error={!!errors.facility?.phone}
                  helperText={errors.facility?.phone?.message}
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <Controller
              name="facility.url"
              control={control}
              render={({ name, value, ref, onChange }) => (
                <TextField
                  name={name}
                  data-cy="facility.url"
                  label="Website (optional)"
                  variant="outlined"
                  className={styles.facilityInput}
                  error={!!errors.facility?.url}
                  helperText={
                    errors.facility?.url?.message ||
                    'Enter the full address (https://examplegolfcourse.com)'
                  }
                  inputRef={ref}
                  disabled={preventExternalFacilityEdit}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </Grid>
        </Grid>
        <Controller
          name="facility.private"
          control={control}
          render={({ onChange, value, name }) => (
            <FormControlLabel
              label="This is a private facility"
              control={
                <Checkbox
                  color="primary"
                  name={name}
                  checked={value}
                  disabled={preventExternalFacilityEdit}
                  onChange={e => onChange(e.target.checked)}
                />
              }
            />
          )}
        />
      </ContentSection>
    </>
  )
}

const FacilitiesForm = ({
  facility,
  facilityPersisted,
  preventExternalFacilityEdit,
  externalFacilityId,
  onSubmit,
  loading,
}) => {
  const defaultValues = facility
    ? { facility }
    : {
      facility: {
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        url: '',
        private: false,
      },
    }

  const form = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })
  const styles = useStyles()
  const [facilitySelected, setFacilitySelected] = useState(externalFacilityId)

  const { register, errors, control, setValue, getValues, handleSubmit } = form

  const formValues = getValues()

  const setFacility = hit => {
    const {
      name,
      address1,
      city,
      state,
      zip,
      phone,
      website: url,
      operations_type_label: typeLabel,
      facility_id: externalFacilityId,
    } = hit

    setValue(
      'facility',
      {
        ...formValues,
        name,
        address1,
        city,
        state,
        zip,
        phone,
        externalFacilityId,
        url: url && !/^https?:\/\//i.test(url) ? `http://${url}` : url,
        private:
          typeLabel === 'Private Equity' || typeLabel === 'Private Non-Equity',
        customData: {
          ...formValues.customData,
          offersCoachingAtPrivateClub: false,
        },
      },
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    )

    setFacilitySelected(externalFacilityId)
  }

  const clearSelectedFacility = () => {
    setValue('facility', {
      ...formValues,
      externalFacilityId: null,
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      url: '',
      private: false,
      customData: {
        ...formValues.customData,
        offersCoachingAtPrivateClub: false,
      },
    })

    setFacilitySelected(null)
  }

  return (
    <>
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>

      <FormActionBar
        includeCancel
        instructions={errors.length > 0 && 'Form has one or more errors'}
        error={errors.length > 0}
      >
        <div className={styles.buttonWrapper}>
          <Button
            onClick={handleSubmit(onSubmit)}
            color="primary"
            variant="contained"
            size="large"
          >
            Save
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              className={styles.buttonProgress}
              color="secondary"
            />
          )}
        </div>
      </FormActionBar>

      <Container>
        <FormSections
          control={control}
          setFacility={setFacility}
          register={register}
          errors={errors}
          preventExternalFacilityEdit={
            !!facilitySelected && preventExternalFacilityEdit
          }
          facilityPersisted={!!facilityPersisted}
          clearSelectedFacility={clearSelectedFacility}
        />
      </Container>
    </>
  )
}

export default FacilitiesForm
